import { CompositeFilterDescriptor, State } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import { Dialog } from "@progress/kendo-react-dialogs";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { debounce } from "lodash";
import { Plus } from "lucide-react";
import { createRef, useCallback, useEffect, useRef, useState } from "react";
import Export from "../../component/global/Export";
import SearchBox from "../../component/ui/SearchBox";
import Filters from "../../constants/filters";
import { ICompany } from "../../interfaces/company.interface";
import IPageState from "../../interfaces/page.interface";
import { ISearchFilter } from "../../interfaces/searchFilter.interface";
import companySvc from "../../services/company.service";
import { setToast } from "../../store/features/toastSlice";
import { useAppDispatch } from "../../store/hooks/hooks";
import { PROJECT_ADMIN } from "../../utils/roles.constant";
import { getPaginationData, isAuthorize } from "../../utils/utils";
import WriteDrawer from "./WriteDrawer";
import { columnInterface, generateColumns } from "./columns";

let mappingChanged = false;

export const Listing = () => {
  const _exporter = createRef<ExcelExport>();
  const excelExport = () => {
    if (_exporter.current) {
      fetchAllCompanies();
    }
  };
  const dispatch = useAppDispatch();

  const Pagination = getPaginationData();

  const debouncedSearch = useRef(
    debounce((value: string) => fetchCompanies(value), 500)
  ).current;

  const [showDialog, setShowDialog] = useState(false);

  const [addNewCompany, setAddNewCompany] = useState<
    (ICompany & { id: string }) | null
  >(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [dataState, setDataState] = useState<State>(
    Pagination.initialDataState
  );
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [searchValue, setSearchValue] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [companies, setCompanies] = useState<any>([]);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(
      handleDownload,
      handlePreview,
      handleEnableDisable,
      fetchCompaniesWrapper,
      handleEditClick,
      onColumnsSubmit,
      null,
      columnsMapping,
      mappingChanged
    )
  );
  const [total, setTotal] = useState(0);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      handleDownload,
      handlePreview,
      handleEnableDisable,
      fetchCompaniesWrapper,
      handleEditClick,
      onColumnsSubmit,
      columnsState,
      columnsMapping,
      mappingChanged
    );
    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? companies.length : event.page.take;

    fetchCompanies(searchValue, null, event.page.skip, event.page.take);
    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (value?.length !== 1) {
      debouncedSearch(value);
    }
  };

  const handleChange = (e: TextBoxChangeEvent) => {
    setAddNewCompany((prev: any) => {
      return { ...prev, [e.target.name as any]: e.target.value };
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
    fetchCompanies(
      searchValue,
      event.dataState.filter,
      event.dataState.sort,
      page.skip,
      page.take
    );
  };

  const createCompany = async () => {
    if (logoFile) {
      const filePath = `Logo/${logoFile.name}`;
      const uploadRes = await companySvc.upload(logoFile, filePath);
      if (!uploadRes.error) {
        addNewCompany!.logo = uploadRes.data;
      }
    }
    companySvc.create({ ...addNewCompany, isActive: true }).then((res) => {
      fetchCompanies(searchValue);
      setAddNewCompany(null);
      setLogoFile(null);
      setShowDrawer(false);
    });
  };

  const editCompany = async () => {
    if (!addNewCompany?.id) return;
    if (logoFile) {
      const filePath = `Logo/${logoFile.name}`;
      const uploadRes = await companySvc.upload(logoFile, filePath);
      if (!uploadRes.error) {
        addNewCompany!.logo = uploadRes.data;
      }
    }
    companySvc.update(addNewCompany.id, addNewCompany).then((res) => {
      if (!res.error) {
        dispatch(
          setToast({
            toastType: "success",
            toastMsg: "User updated successfully",
          })
        );
      }
      fetchCompanies(searchValue);

      setAddNewCompany(null);
      setLogoFile(null);
      setShowDrawer(false);
    });
  };

  function onCancel() {
    // fetchCompanies(searchValue);
    setAddNewCompany(null);
    setLogoFile(null);
    setShowDrawer(false);
  }

  function handleEditClick(data: ICompany) {
    setAddNewCompany(data);
    setLogoFile(null);
    setShowDrawer(true);
  }

  function handleEnableDisable(data: ICompany) {
    if(!isAuthorize([PROJECT_ADMIN])) {
      dispatch(
        setToast({
          toastType: "error",
          toastMsg: "You are not authorized to perform this action",
        }));
      return;
    }
    companySvc
      .update(data.id, {
        ...data,
        isActive: data.isActive ? false : true,
      })
      .then((x) => {
        fetchCompanies(searchValue);
      });
  }

  const fetchCompanies = useCallback(
    async (
      searchQuery = "",
      filter?: CompositeFilterDescriptor | null,
      sort?: any,
      pageNumber = 0,
      pageSize = Pagination.initialDataState.take
    ) => {
      let searchFilters: ISearchFilter[] = [];
      if (searchQuery) {
        searchFilters = [
          { field: "Name", operator: "contains", value: searchQuery },
          { field: "Code", operator: "contains", value: searchQuery },
        ];
      }
      const res = await companySvc.get(
        searchFilters,
        filter,
        sort,
        pageNumber,
        pageSize
      );
      if (res.data) {
        setCompanies(res.data);
        setTotal(res.data.total);
      }
    },
    []
  );

  const fetchAllCompanies = async () => {
    companySvc.getAll().then((res) => {
      setAllCompanies(res.data);
    });
  };

  useEffect(() => {
    if (companies.length) return;
    fetchCompanies(searchValue);
  }, []);

  useEffect(() => {
    if (!companies?.length || !columnsMapping) return;
    const cols = generateColumns(
      handleDownload,
      fetchCompaniesWrapper,
      handleEditClick,
      onColumnsSubmit,
      null,
      columnsMapping,
      mappingChanged
    );
    setStateColumns(cols);
  }, [companies, columnsMapping]);

  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (!allCompanies?.length) return;
    _exporter.current?.save();
    setAllCompanies([]);
  }, [allCompanies]);

  function fetchCompaniesWrapper() {
    fetchCompanies(searchValue);
  }

  function handleDownload(fileUrl: string, name: string) {
    const link = document.createElement("a");
    // console.log("BASE 64: ", base64);
    link.href = fileUrl;

    link.download = name + ".png";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  }

  function handlePreview(fileUrl: string) {
    if (fileUrl) setShowDialog(!showDialog);
    setImageUrl(fileUrl);
  }

  async function handleFileUpload(e: any) {
    const files = e.affectedFiles;
    if (files.length > 0) {
      setLogoFile(files[0].getRawFile());

      const base64: any = await fileToBase64(files[0].getRawFile());
      if (base64) {
        setAddNewCompany((prev: any) => {
          return { ...prev, logoPath: base64 };
        });
      }
    }
  }

  function fileToBase64(file: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  useEffect(() => {
    if (!showDrawer) {
      setAddNewCompany(null);
      setLogoFile(null);
    }
  }, [showDrawer]);

  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={companies}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={total}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
          // columnMenu={ColumnMenu}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              {isAuthorize([PROJECT_ADMIN]) && <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => {
                  setLogoFile(null);
                  setShowDrawer(true);
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Add New
                </span>
              </Button>}
              <div
                className="cursor-pointer  sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={allCompanies}
                  collapsible={true}
                  fileName="Object Types.xlsx"
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      // width={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    // width={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
        {showDialog && (
          <Dialog
            width={500}
            title={"Shape Image"}
            onClose={() => setShowDialog(false)}
            className="custom-dialog image-dialog"
          >
            <div>
              <img src={imageUrl} className="w-full"></img>
            </div>
          </Dialog>
        )}
      </div>
      <WriteDrawer
        handleFileUpload={handleFileUpload}
        handlePreview={handlePreview}
        handleDownload={handleDownload}
        showCreateDrawer={showDrawer}
        setShowCreateDrawer={setShowDrawer}
        addNewCompany={addNewCompany}
        setAddNewCompany={setAddNewCompany}
        handleChange={handleChange}
        onSave={addNewCompany?.id ? editCompany : createCompany}
        setLogoFile={setLogoFile}
        logoFile={logoFile}
        onCancel={onCancel}
      />
    </>
  );
};
